import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { includes } from 'lodash-es';

import { BasicTable, Paper } from '@dev/ee-components';
import { AccessLevel, CustomField } from '@dev/ee-api/ee-api-models';

import { getCustomFieldsStore } from '@/store/customFields/actions';
import { hasAccessLevel } from '@/services/Secuirty';
import { SearchComponent } from '../Search';
import { PaginationComponent } from '../PaginationComponent';
import { useTableRows } from '../hooks/useTableRows';
import { CustomFieldsTableProps } from '../CustomFields';

const CustomTableContainer = styled.div`
    padding: 32px 48px;
    & tr {
        cursor: initial;
    }
`;

const useVisibleFields = (search: string): CustomField[] => {
    const { collection } = useSelector(getCustomFieldsStore);

    if (!collection) {
        return [];
    }
    let visibleFields: CustomField[];
    if (search) {
        visibleFields = collection.filter((field): boolean => includes(field.Name, search));
    } else {
        visibleFields = collection;
    }
    return visibleFields;
};

export const CustomFieldsTable = (props: CustomFieldsTableProps): JSX.Element => {
    const { t } = useTranslation();
    const { isLoadingFields } = useSelector(getCustomFieldsStore);
    const { activePage, setActivePage, perPage, setPerPage, search, setSearch } = props;
    const newVisibleSet = useVisibleFields(search);
    const sliceVisibleSet = newVisibleSet.slice(perPage * (+activePage - 1), perPage * activePage);
    const rows = useTableRows(sliceVisibleSet);

    const canModifyCustomFields = hasAccessLevel(AccessLevel.ModifyCustomFields);
    //create headers row
    const headerRow: { id: string; label: string; disablePadding?: boolean; columnAlign?: 'left' | 'right' }[] = [
        { id: 'fieldname', label: t('fieldname'), disablePadding: true, columnAlign: 'left' },
        { id: 'datatype', label: t('datatype') },
        { id: 'details', label: t('details') },
    ];

    if (canModifyCustomFields) {
        headerRow.push({ id: 'options', label: '', disablePadding: true, columnAlign: 'right' });
    }
    return (
        <Paper padding={{ top: 24 }}>
            <SearchComponent search={search} setSearch={setSearch} setActivePage={setActivePage} />
            <CustomTableContainer>
                <BasicTable
                    columnWidth={{
                        fieldname: '200px',
                        datatype: '200px',
                    }}
                    headers={headerRow}
                    rows={rows}
                    isEmpty={rows.length === 0 && !isLoadingFields}
                    stickyHeader
                    emptyStateProps={{
                        text: t('emptystate'),
                    }}
                />
            </CustomTableContainer>
            <PaginationComponent
                activePage={activePage}
                setActivePage={setActivePage}
                perPage={perPage}
                setPerPage={setPerPage}
                visibleSet={newVisibleSet}
            />
        </Paper>
    );
};
